<template>
  <AdminLayout>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Header -->
      <div class="mb-6 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-[#22282F]/10 rounded-lg">
            <MapPin class="w-7 h-7 text-[#22282F]" />
          </div>
          <div>
            <h1 class="text-2xl sm:text-3xl font-bold text-gray-900">Suivi des courriers</h1>
            <p class="text-sm text-gray-500">Où se trouvent les courriers : chez qui, à quel niveau et pourquoi.</p>
          </div>
        </div>
        <button @click="fetchData" class="px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:opacity-90">
          Actualiser
        </button>
      </div>

      <StatsFilterBar @change="onFilterChange" />

      <div v-if="loading" class="py-16 text-center text-gray-400">Chargement…</div>

      <template v-else-if="data">
        <!-- Cartes résumé -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
          <div class="bg-white rounded-xl border border-gray-200 p-5">
            <p class="text-xs uppercase tracking-wide text-gray-500">En cours</p>
            <p class="text-3xl font-bold text-[#1d2a5e]">{{ data.resume.en_cours }}</p>
          </div>
          <div class="bg-white rounded-xl border border-gray-200 p-5">
            <p class="text-xs uppercase tracking-wide text-gray-500">Finalisés</p>
            <p class="text-3xl font-bold text-emerald-600">{{ data.resume.finalises }}</p>
          </div>
          <div class="bg-white rounded-xl border border-gray-200 p-5">
            <p class="text-xs uppercase tracking-wide text-gray-500">Courriers suivis</p>
            <p class="text-3xl font-bold text-gray-700">{{ data.resume.total_suivis }}</p>
          </div>
        </div>

        <!-- Répartition par rôle (hiérarchie décroissante) -->
        <div class="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h2 class="font-semibold text-gray-800 mb-1">Répartition par rôle</h2>
          <p class="text-xs text-gray-400 mb-4">Du plus haut niveau hiérarchique au plus bas.</p>
          <div v-if="(data.par_role || []).length" class="space-y-3">
            <div v-for="r in data.par_role" :key="r.role_code || r.role" class="flex items-center gap-3">
              <div class="w-52 flex items-center gap-2">
                <span class="text-[10px] font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">N{{ r.niveau ?? '?' }}</span>
                <span class="text-sm text-gray-700 truncate" :title="r.role">{{ r.role }}</span>
              </div>
              <div class="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                <div class="h-5 bg-[#1d2a5e] rounded-full" :style="{ width: barWidth(r.count, maxRole) }"></div>
              </div>
              <div class="w-12 text-right text-sm font-bold text-[#1d2a5e]">{{ r.count }}</div>
            </div>
          </div>
          <p v-else class="text-sm text-gray-400">Aucun courrier en cours sur ce périmètre.</p>
        </div>

        <!-- Par département / direction / ambassade -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <DimTable title="Par département" :rows="data.par_departement" />
          <DimTable title="Par direction" :rows="data.par_direction" />
          <DimTable title="Par ambassade" :rows="data.par_ambassade" />
        </div>

        <!-- Par étape (niveau + motif) -->
        <div class="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h2 class="font-semibold text-gray-800 mb-4">Répartition par étape</h2>
          <div v-if="(data.par_niveau || []).length" class="space-y-3">
            <div v-for="n in data.par_niveau" :key="n.label" class="flex items-center gap-3">
              <div class="w-44 text-sm text-gray-700 truncate" :title="n.label">{{ n.label }}</div>
              <div class="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                <div class="h-5 rounded-full" :class="stageBarClass(n.statut)" :style="{ width: barWidth(n.count, maxNiveau) }"></div>
              </div>
              <div class="w-12 text-right text-sm font-bold text-[#1d2a5e]">{{ n.count }}</div>
              <div class="w-64 text-xs text-gray-400 truncate" :title="n.motif">{{ n.motif }}</div>
            </div>
          </div>
          <p v-else class="text-sm text-gray-400">Aucune donnée.</p>
        </div>

        <!-- Détail ligne-à-ligne -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div class="px-5 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 class="font-semibold text-gray-800">Détail des courriers en cours</h2>
            <div class="flex items-center gap-2">
              <div class="relative">
                <Search class="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  v-model="search"
                  type="text"
                  placeholder="Rechercher (n°, objet, détenteur…)"
                  class="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm w-64"
                />
              </div>
              <span class="text-xs text-gray-400 whitespace-nowrap">{{ filteredDetail.length }} résultat(s)</span>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-gray-50 text-gray-500">
                <tr>
                  <th class="text-left px-4 py-2 font-medium">Courrier</th>
                  <th class="text-left px-4 py-2 font-medium">Détenteur</th>
                  <th class="text-left px-4 py-2 font-medium">Direction / Ambassade</th>
                  <th class="text-left px-4 py-2 font-medium">Étape</th>
                  <th class="text-right px-4 py-2 font-medium">Depuis</th>
                  <th class="text-left px-4 py-2 font-medium">Pourquoi</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in pageRows" :key="i" class="border-t border-gray-100 hover:bg-gray-50/60">
                  <td class="px-4 py-2 align-top">
                    <div class="font-medium text-gray-800">#{{ r.courrier_id }}</div>
                    <div v-if="r.objet" class="text-xs text-gray-400 max-w-[220px] truncate" :title="r.objet">{{ r.objet }}</div>
                  </td>
                  <td class="px-4 py-2 align-top">{{ r.detenteur || '-' }}</td>
                  <td class="px-4 py-2 align-top text-gray-500">{{ r.direction || r.ambassade || '-' }}</td>
                  <td class="px-4 py-2 align-top">
                    <span class="px-2 py-0.5 rounded-full text-xs whitespace-nowrap" :class="stagePillClass(r.statut)">{{ r.etape }}</span>
                  </td>
                  <td class="px-4 py-2 align-top text-right whitespace-nowrap"
                      :class="agingClass(r.depuis_jours)">
                    {{ r.depuis_jours != null ? r.depuis_jours + ' j' : '-' }}
                  </td>
                  <td class="px-4 py-2 align-top">
                    <div class="flex items-start gap-2 max-w-[320px]">
                      <span class="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" :class="stageDotClass(r.statut)"></span>
                      <div class="min-w-0">
                        <div class="text-gray-700">{{ r.motif || '-' }}</div>
                        <div v-if="r.commentaire" class="text-xs text-gray-400 italic truncate" :title="r.commentaire">
                          « {{ r.commentaire }} »
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr v-if="!filteredDetail.length">
                  <td colspan="6" class="px-4 py-8 text-center text-gray-400">Aucun courrier en cours.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Pagination -->
          <div v-if="totalPages > 1" class="px-5 py-3 border-t border-gray-100 flex items-center justify-between text-sm">
            <button
              @click="page = Math.max(1, page - 1)"
              :disabled="page === 1"
              class="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
            >Précédent</button>
            <span class="text-gray-500">Page {{ page }} / {{ totalPages }}</span>
            <button
              @click="page = Math.min(totalPages, page + 1)"
              :disabled="page === totalPages"
              class="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
            >Suivant</button>
          </div>
        </div>
      </template>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, h } from 'vue'
import { MapPin, Search } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import StatsFilterBar from '@/components/dashboard/StatsFilterBar.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

const toast = useToast()
const loading = ref(false)
const data = ref<any>(null)
const filters = ref<Record<string, string>>({})

const search = ref('')
const page = ref(1)
const PAGE_SIZE = 20

const maxRole = computed(() =>
  Math.max(1, ...((data.value?.par_role || []).map((r: any) => r.count))))
const maxNiveau = computed(() =>
  Math.max(1, ...((data.value?.par_niveau || []).map((n: any) => n.count))))

function barWidth(v: number, max: number) {
  return `${Math.max(4, Math.round((v / max) * 100))}%`
}

// ── Couleurs par étape (statut normalisé) ───────────────────────────
function stageKey(s: string) {
  const k = (s || '').toLowerCase()
  if (k.includes('attente')) return 'attente'
  if (k.includes('redaction') || k.includes('rdaction')) return 'redaction'
  if (k.includes('traitement')) return 'traitement'
  if (k.includes('consultation')) return 'consultation'
  if (k.includes('accepte')) return 'accepte'
  return 'autre'
}
const PILL: Record<string, string> = {
  attente: 'bg-amber-100 text-amber-700',
  redaction: 'bg-blue-100 text-blue-700',
  traitement: 'bg-indigo-100 text-indigo-700',
  consultation: 'bg-purple-100 text-purple-700',
  accepte: 'bg-teal-100 text-teal-700',
  autre: 'bg-gray-100 text-gray-600',
}
const DOT: Record<string, string> = {
  attente: 'bg-amber-400',
  redaction: 'bg-blue-500',
  traitement: 'bg-indigo-500',
  consultation: 'bg-purple-500',
  accepte: 'bg-teal-500',
  autre: 'bg-gray-400',
}
const BAR: Record<string, string> = {
  attente: 'bg-amber-400',
  redaction: 'bg-blue-500',
  traitement: 'bg-indigo-500',
  consultation: 'bg-purple-500',
  accepte: 'bg-teal-500',
  autre: 'bg-[#1d2a5e]',
}
const stagePillClass = (s: string) => PILL[stageKey(s)]
const stageDotClass = (s: string) => DOT[stageKey(s)]
const stageBarClass = (s: string) => BAR[stageKey(s)]

function agingClass(days: number | null) {
  if (days == null) return 'text-gray-400'
  if (days >= 7) return 'text-red-600 font-semibold'
  if (days >= 3) return 'text-amber-600'
  return 'text-gray-500'
}

// ── Recherche + pagination (côté client) ────────────────────────────
const filteredDetail = computed(() => {
  const rows = data.value?.detail || []
  const q = search.value.trim().toLowerCase()
  if (!q) return rows
  return rows.filter((r: any) =>
    [`#${r.courrier_id}`, r.courrier_id, r.objet, r.detenteur, r.direction,
     r.ambassade, r.etape, r.motif, r.commentaire]
      .filter(Boolean)
      .some((v: any) => String(v).toLowerCase().includes(q)))
})
const totalPages = computed(() => Math.max(1, Math.ceil(filteredDetail.value.length / PAGE_SIZE)))
const pageRows = computed(() => filteredDetail.value.slice((page.value - 1) * PAGE_SIZE, page.value * PAGE_SIZE))

watch([search, filteredDetail], () => { if (page.value > totalPages.value) page.value = 1 })

function queryString() {
  const p = new URLSearchParams()
  Object.entries(filters.value).forEach(([k, v]) => { if (v) p.append(k, v) })
  const s = p.toString()
  return s ? `?${s}` : ''
}

async function fetchData() {
  loading.value = true
  try {
    data.value = await apiFetch(`/api/admin/stats/positions/${queryString()}`)
    page.value = 1
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Chargement impossible')
  } finally {
    loading.value = false
  }
}

function onFilterChange(params: Record<string, string>) {
  filters.value = params || {}
  fetchData()
}

onMounted(fetchData)

// Petit tableau de dimension (département / direction / ambassade).
const DimTable = (props: any) => {
  const rows = props.rows || []
  return h('div', { class: 'bg-white rounded-xl border border-gray-200 p-5' }, [
    h('h2', { class: 'font-semibold text-gray-800 mb-3' }, props.title),
    rows.length
      ? h('table', { class: 'min-w-full text-sm' }, [
          h('tbody', rows.map((r: any) =>
            h('tr', { class: 'border-t border-gray-100' }, [
              h('td', { class: 'py-2 pr-2 text-gray-700' }, r.label || '-'),
              h('td', { class: 'py-2 pl-2 text-right font-bold text-[#1d2a5e]' }, r.count),
            ])))
        ])
      : h('p', { class: 'text-sm text-gray-400' }, 'Aucune donnée.'),
  ])
}
</script>
